
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, Activity, Calendar, Settings, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ActivityFormProps {
  activity?: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const ActivityForm = ({ activity, open, onOpenChange, onSuccess }: ActivityFormProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_time: '',
    end_time: '',
    max_capacity: 20,
    status: 'active',
    days_of_week: [] as number[],
    requires_day_selection: false
  });
  const { toast } = useToast();

  const daysOfWeek = [
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' }
  ];

  // Reset form when modal opens/closes or activity changes
  useEffect(() => {
    if (activity) {
      setFormData({
        name: activity.name || '',
        description: activity.description || '',
        start_time: activity.start_time || '',
        end_time: activity.end_time || '',
        max_capacity: activity.max_capacity || 20,
        status: activity.status || 'active',
        days_of_week: activity.days_of_week || [],
        requires_day_selection: activity.requires_day_selection || false
      });
    } else {
      setFormData({
        name: '',
        description: '',
        start_time: '',
        end_time: '',
        max_capacity: 20,
        status: 'active',
        days_of_week: [],
        requires_day_selection: false
      });
    }
  }, [activity, open]);

  const handleDayChange = (dayValue: number, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        days_of_week: [...prev.days_of_week, dayValue].sort()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        days_of_week: prev.days_of_week.filter(day => day !== dayValue)
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "El nombre de la actividad es obligatorio.",
        variant: "destructive"
      });
      return false;
    }

    if (!formData.start_time || !formData.end_time) {
      toast({
        title: "Error",
        description: "Debe especificar la hora de inicio y fin.",
        variant: "destructive"
      });
      return false;
    }

    if (formData.start_time >= formData.end_time) {
      toast({
        title: "Error",
        description: "La hora de fin debe ser posterior a la hora de inicio.",
        variant: "destructive"
      });
      return false;
    }

    if (formData.days_of_week.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un día de la semana.",
        variant: "destructive"
      });
      return false;
    }

    if (formData.max_capacity < 1) {
      toast({
        title: "Error",
        description: "La capacidad debe ser mayor a 0.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);

      const activityData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        start_time: formData.start_time,
        end_time: formData.end_time,
        max_capacity: formData.max_capacity,
        status: formData.status,
        days_of_week: formData.days_of_week,
        requires_day_selection: formData.requires_day_selection,
        updated_at: new Date().toISOString()
      };

      if (activity) {
        // Actualizar actividad existente
        const { error } = await supabase
          .from('activities')
          .update(activityData)
          .eq('id', activity.id);

        if (error) throw error;

        toast({
          title: "Actividad actualizada",
          description: `${formData.name} ha sido actualizada exitosamente.`
        });
      } else {
        // Crear nueva actividad
        const { error } = await supabase
          .from('activities')
          .insert(activityData);

        if (error) throw error;

        toast({
          title: "Actividad creada",
          description: `${formData.name} ha sido creada exitosamente.`
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar la actividad.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {activity ? (
              <>
                <Calendar className="w-5 h-5" />
                Editar Actividad
              </>
            ) : (
              <>
                <Activity className="w-5 h-5" />
                Nueva Actividad
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre de la actividad *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej. Fútbol, Teatro, Ajedrez..."
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción opcional de la actividad..."
                rows={3}
              />
            </div>
          </div>

          {/* Nota informativa sobre precios */}
          <Card className="border-2 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="w-5 h-5 text-blue-600" />
                Próximo paso: Configurar precios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-blue-100 dark:bg-blue-950/30 rounded-md border border-blue-300 dark:border-blue-700">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 Después de crear la actividad, ve a la sección de <strong>Finanzas</strong> para configurar los precios por día y/o cuotas mensuales. Los padres podrán elegir su modalidad de pago preferida al inscribir a sus hijos.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Horarios */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_time">Hora de inicio *</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="end_time">Hora de fin *</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                required
              />
            </div>
          </div>

          {/* Días de la semana */}
          <div>
            <Label>Días de la semana *</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {daysOfWeek.map((day) => (
                <div key={day.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${day.value}`}
                    checked={formData.days_of_week.includes(day.value)}
                    onCheckedChange={(checked) => handleDayChange(day.value, checked as boolean)}
                  />
                  <Label htmlFor={`day-${day.value}`} className="text-sm">
                    {day.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Configuración especial de selección de días */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="requires_day_selection" className="text-sm font-medium flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Permitir selección de días específicos
                </Label>
                <p className="text-xs text-muted-foreground">
                  Activar para actividades como Comedor o Madrugadores donde los estudiantes no asisten todos los días.
                </p>
              </div>
              <Switch
                id="requires_day_selection"
                checked={formData.requires_day_selection}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_day_selection: checked }))}
              />
            </div>
            {formData.requires_day_selection && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ Con esta opción activada, los padres podrán seleccionar qué días específicos quieren que sus hijos asistan a esta actividad.
                </p>
              </div>
            )}
          </div>

          {/* Configuración adicional */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="max_capacity">Capacidad máxima *</Label>
              <Input
                id="max_capacity"
                type="number"
                min="1"
                value={formData.max_capacity}
                onChange={(e) => setFormData(prev => ({ ...prev, max_capacity: parseInt(e.target.value) || 1 }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="status">Estado</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border z-50">
                  <SelectItem value="active">Activa</SelectItem>
                  <SelectItem value="upcoming">Próximamente</SelectItem>
                  <SelectItem value="completed">Finalizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {activity ? 'Actualizar' : 'Crear'} Actividad
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
